import React from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import {
  updatePlan,
  setSelected,
} from '../../../PlanSelectionNewWhatIfs/PlanSelectionNewWhatIfsActions';
import { PlanForm } from '../PlanForm/PlanForm';
import { FormValues, EDIT_PLAN_FORM_ID } from '../PlanForm/PlanForm.meta';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { useEditPlanMutation } from '../../api/useEditPlanMutation';
import { useSelectedPlan } from '../../hooks/useSelectedPlan';
import { useRouteParams } from '../../hooks/useRouteParams';
import { queryKeys } from '../../api/queryKeys';

interface Props {
  closeModal: () => void;
}

export const EditPlanModal: React.FC<Props> = ({ closeModal }) => {
  const selectedPlan = useSelectedPlan();

  const { clientId } = useRouteParams();

  const dispatch = useDispatch();

  const { mutateAsync: editPlan, isLoading: isEditingPlan } = useEditPlanMutation();

  const queryClient = useQueryClient();

  if (!selectedPlan) {
    return null;
  }

  const {
    description: selectedPlanDescription,
    name: selectedPlanName,
    isCurrent: selectedPlanIsCurrent,
  } = selectedPlan;

  const handleEditPlan = async ({ name, description }: FormValues) => {
    const updatedPlan = {
      ...selectedPlan,
      name,
      description,
    };

    try {
      await editPlan({
        clientId,
        PlansToUpdate: [updatedPlan],
      });

      //  TODO (LEGACY): Remove after the migration is complete
      dispatch(updatePlan(updatedPlan, clientId));
      dispatch(setSelected(updatedPlan.planId, clientId));

      queryClient.invalidateQueries(queryKeys.proposedPlans(clientId));

      toast.success('Plan is edited successfully.');
    } catch (err) {
      toast.error('An error occurred. Please try again later.');
    }

    closeModal();
  };

  return (
    <Modal
      isOpen
      onRequestClose={closeModal}
      aria={{ labelledby: 'plan-selection--edit-plan-modal-heading' }}
    >
      <ModalHeader id="plan-selection--edit-plan-modal-heading">Edit Plan</ModalHeader>

      <ModalCloseButton disabled={isEditingPlan} onClick={closeModal} />

      <ModalBody>
        <PlanForm
          formId={EDIT_PLAN_FORM_ID}
          onSubmit={handleEditPlan}
          defaultName={selectedPlanName}
          defaultDescription={selectedPlanDescription}
          isDisabled={selectedPlanIsCurrent}
        />
      </ModalBody>

      <ModalFooter>
        <Box display="flex" justifyContent="flex-end">
          <Button disabled={isEditingPlan} variant="subtleInactive" onClick={closeModal}>
            Cancel
          </Button>

          <Button type="submit" isLoading={isEditingPlan} ml="12" form={EDIT_PLAN_FORM_ID}>
            Save Plan
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
